import { Button, Typography } from "antd";
import dayjs from "dayjs";
import { CalendarClock, Link2, PhoneIncoming, PhoneOutgoing, Video, type LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { CalendarEventStatus } from "@fitness-app/data-models/entities/CalendarEvent";
import { cn } from "@fitness-app/utils/src/styles/cn";

import { CalendarChip } from "~/components/EventCalendar/CalendarChip";
import { formatLongDay, type RenderEventOptions } from "~/components/EventCalendar/calendarModel";
import {
  DetailRow,
  EventPopover,
  EventPopoverCard,
  StatusPill,
  type PopoverTone,
} from "~/components/EventCalendar/EventPopover";
import { calendarEventStatusLabels } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeCalendar/components/CalendarEventStatusBadge";
import {
  CHANNEL_LABELS,
  getClientName,
  getConsultationContact,
  STATUS_CHIP_TONES,
  STATUS_PILL_TONES,
  toTelHref,
  type ConsultationChannel,
  type TrainerConsultation,
} from "./consultationModel";

const CHANNEL_ICONS: Record<ConsultationChannel, LucideIcon> = {
  outboundCall: PhoneOutgoing,
  inboundCall: PhoneIncoming,
  zoom: Video,
  link: Link2,
  other: CalendarClock,
};

const STATUS_POPOVER_TONES: Record<CalendarEventStatus, PopoverTone> = {
  [CalendarEventStatus.ACTIVE]: "blue",
  [CalendarEventStatus.ATTENDED]: "emerald",
  [CalendarEventStatus.NO_SHOW]: "amber",
  [CalendarEventStatus.CANCELED]: "gray",
};

const LINK_CLASS_NAME = "block truncate";

const ConsultationDetails = ({ consultation }: { consultation: TrainerConsultation }) => {
  const navigate = useNavigate();
  const { trainee } = consultation;
  const contact = getConsultationContact(consultation);
  const Icon = CHANNEL_ICONS[contact.channel];
  const start = dayjs(consultation.startTime);
  const end = dayjs(consultation.endTime);
  const cancellation = consultation.status === CalendarEventStatus.CANCELED ? consultation.cancellation : null;

  return (
    <EventPopoverCard
      icon={<Icon />}
      tone={STATUS_POPOVER_TONES[consultation.status]}
      title={getClientName(consultation)}
      subtitle={`${formatLongDay(start)}, ${start.format("HH:mm")} - ${end.format("HH:mm")}`}
      badge={
        <StatusPill tone={STATUS_PILL_TONES[consultation.status]}>
          {calendarEventStatusLabels[consultation.status]}
        </StatusPill>
      }
      footer={
        trainee && (
          <Button size="small" onClick={() => navigate(`/trainee/details/${trainee.id}`)}>
            Profil klientki
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-2.5">
        <DetailRow label="Forma">{CHANNEL_LABELS[contact.channel]}</DetailRow>
        {contact.phone && (
          <DetailRow label="Telefon">
            <Typography.Link href={toTelHref(contact.phone)} className={LINK_CLASS_NAME}>
              {contact.phone}
            </Typography.Link>
          </DetailRow>
        )}
        {contact.meetingUrl && (
          <DetailRow label="Link">
            <Typography.Link href={contact.meetingUrl} target="_blank" rel="noreferrer" className={LINK_CLASS_NAME}>
              {contact.meetingUrl}
            </Typography.Link>
          </DetailRow>
        )}
        {!trainee && consultation.clientEmail && (
          <DetailRow label="E-mail">
            <span className="block truncate">{consultation.clientEmail}</span>
          </DetailRow>
        )}
        {consultation.eventName && <DetailRow label="Rodzaj">{consultation.eventName}</DetailRow>}
        {cancellation?.canceled_by && <DetailRow label="Anulowane przez">{cancellation.canceled_by}</DetailRow>}
        {cancellation?.reason && <DetailRow label="Powód">{cancellation.reason}</DetailRow>}
      </div>
    </EventPopoverCard>
  );
};

interface ConsultationChipProps extends Partial<RenderEventOptions> {
  consultation: TrainerConsultation;
}

export const ConsultationChip = ({ consultation, dragIdScope }: ConsultationChipProps) => {
  const { channel } = getConsultationContact(consultation);
  // The trainer calling the client is 93% of bookings, so only the exceptions get an icon: a week
  // column at 1280 px leaves the name ~46 px with it and ~64 px without.
  const Icon = channel === "outboundCall" ? null : CHANNEL_ICONS[channel];

  return (
    <CalendarChip
      dragId={dragIdScope ? `${dragIdScope}:${consultation.id}` : consultation.id}
      dragData={{}}
      canDrag={false}
      toneClassName={STATUS_CHIP_TONES[consultation.status]}
    >
      <EventPopover content={<ConsultationDetails consultation={consultation} />}>
        <div className="gap-1.5">
          {Icon && <Icon size={12} className="opacity-60" />}
          <span className="shrink-0 tabular-nums opacity-70">{dayjs(consultation.startTime).format("HH:mm")}</span>
          <span
            className={cn(
              "min-w-0 flex-1 truncate",
              consultation.status === CalendarEventStatus.CANCELED && "line-through",
            )}
          >
            {getClientName(consultation)}
          </span>
        </div>
      </EventPopover>
    </CalendarChip>
  );
};
